import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["tab", "subNav", "subNavInner", "mainNav"]
  static values = { subcategories: Object }

  connect() {
    this.currentHash = null
    this.visibleSections = new Set()
    this.sectionElements = [...document.querySelectorAll("[id^='cat-']")]
    this.programmaticScroll = false
    this.activeRootCatId = null

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.visibleSections.add(entry.target.id)
          } else {
            this.visibleSections.delete(entry.target.id)
          }
        })
        this.syncActive()
      },
      { rootMargin: "-100px 0px -60% 0px" }
    )

    this.sectionElements.forEach((el) => this.observer.observe(el))

    // サブカテゴリセクションの監視用
    this.subSectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.visibleSubSections.add(entry.target.id)
          } else {
            this.visibleSubSections.delete(entry.target.id)
          }
        })
        this.updateActiveSubButton()
      },
      { rootMargin: "-180px 0px -60% 0px" }
    )
    this.visibleSubSections = new Set()
    this.observedSubSections = []

    this.onScroll = () => this.syncActive()
    window.addEventListener("scroll", this.onScroll, { passive: true })
  }

  disconnect() {
    this.observer?.disconnect()
    this.subSectionObserver?.disconnect()
    window.removeEventListener("scroll", this.onScroll)
    this._clearSettleWatcher()
    if (this._scrollEndTimer) clearTimeout(this._scrollEndTimer)
    if (this._scrollEndListener) window.removeEventListener("scrollend", this._scrollEndListener)
  }

  // スクロール位置からアクティブにすべきカテゴリを決めて反映する
  // IntersectionObserver と scroll の両方からここを呼ぶことで、判定が食い違って
  // サブカテゴリバーが出たり消えたりを繰り返す（画面が上下にガタつく）のを防ぐ
  syncActive() {
    if (this.programmaticScroll) return
    const hash = this.resolveActiveHash()
    if (hash) this.activateByHash(hash)
  }

  resolveActiveHash() {
    // ページ末尾付近: 最後のカテゴリが短くても選べるように「上端が画面の半分より上にある最後のセクション」
    const nearBottom = (window.innerHeight + window.scrollY) >= (document.documentElement.scrollHeight - 100)
    if (nearBottom) {
      const last = [...this.sectionElements].reverse().find((el) =>
        el.getBoundingClientRect().top < window.innerHeight * 0.5
      )
      if (last) return `#${last.id}`
    }

    // 通常: 表示帯域に入っているセクションのうち一番上のもの
    if (this.visibleSections.size === 0) return null
    const topVisible = this.sectionElements.find((el) => this.visibleSections.has(el.id))
    return topVisible ? `#${topVisible.id}` : null
  }

  select(event) {
    event.preventDefault()
    const href = event.currentTarget.getAttribute("href")
    this.activateByHash(href)

    this._startProgrammaticScroll(href)

    if (href === "#all") {
      window.scrollTo({ top: 0, behavior: "smooth" })
    } else {
      const target = document.querySelector(href)
      if (target) {
        const offset = this.scrollOffset()
        const top = target.getBoundingClientRect().top + window.scrollY - offset
        window.scrollTo({ top, behavior: "smooth" })
      }
    }
  }

  selectSub(event) {
    event.preventDefault()
    const subcatId = event.currentTarget.dataset.subcatId

    if (subcatId === "all") {
      if (this.activeRootCatId) {
        const rootSection = document.querySelector(`#cat-${this.activeRootCatId}`)
        if (rootSection) {
          this._startProgrammaticScroll(`#cat-${this.activeRootCatId}`, "all")
          const offset = this.scrollOffset()
          const top = rootSection.getBoundingClientRect().top + window.scrollY - offset
          window.scrollTo({ top, behavior: "smooth" })
        }
      }
      this.activateSubButton("all")
      return
    }

    const target = document.querySelector(`#subcat-${subcatId}`)
    if (target) {
      const rootHash = this.activeRootCatId ? `#cat-${this.activeRootCatId}` : null
      this._startProgrammaticScroll(rootHash, subcatId)
      const offset = this.scrollOffset()
      const top = target.getBoundingClientRect().top + window.scrollY - offset
      window.scrollTo({ top, behavior: "smooth" })
    }
    this.activateSubButton(subcatId)
  }

  activateByHash(hash) {
    if (this.currentHash === hash) return
    this.currentHash = hash

    this.tabTargets.forEach((tab) => {
      const isActive = tab.getAttribute("href") === hash
      const indicator = tab.querySelector(".category-indicator")
      if (isActive) {
        tab.classList.remove("opacity-40")
        tab.classList.add("opacity-100")
        if (indicator) indicator.style.width = "100%"
        this.scrollTabIntoView(tab)
      } else {
        tab.classList.remove("opacity-100")
        tab.classList.add("opacity-40")
        if (indicator) indicator.style.width = "0"
      }
    })

    // サブカテゴリバーの表示/非表示
    if (hash === "#all") {
      this.hideSubNav()
      this.activeRootCatId = null
    } else {
      const catId = hash.replace("#cat-", "")
      this.showSubNavForCategory(catId)
    }
  }

  showSubNavForCategory(catId) {
    const subcats = this.subcategoriesValue[catId]
    if (!subcats || subcats.length === 0) {
      this.hideSubNav()
      this.activeRootCatId = null
      return
    }

    // 同じカテゴリなら再構築不要
    if (this.activeRootCatId === catId) return
    this.activeRootCatId = catId

    // サブカテゴリボタンを生成
    const container = this.subNavInnerTarget
    container.innerHTML = ""

    // 「全て」ボタン
    const allBtn = document.createElement("button")
    allBtn.className = "subcategory-btn active"
    allBtn.dataset.subcatId = "all"
    allBtn.dataset.action = "click->category-tabs#selectSub"
    allBtn.textContent = this.allLabel()
    container.appendChild(allBtn)

    // 各サブカテゴリボタン
    subcats.forEach((sub) => {
      const btn = document.createElement("button")
      btn.className = "subcategory-btn"
      btn.dataset.subcatId = sub.id
      btn.dataset.action = "click->category-tabs#selectSub"
      btn.textContent = sub.name
      container.appendChild(btn)
    })

    this.setSubNavVisible(true)

    // サブカテゴリセクションの監視を再設定
    this.setupSubSectionObserver(subcats)
  }

  hideSubNav() {
    this.setSubNavVisible(false)
    this.clearSubSectionObserver()
  }

  // サブナビは通常フロー内の sticky 要素なので、出し入れするとその高さ分だけ下のコンテンツが動く。
  // ナビが画面上部に張り付いている状態ならスクロール位置を同じ分だけ補正して、見た目が動かないようにする
  // （このズレが scroll / IntersectionObserver を再発火させてチラつきの原因になっていた）
  setSubNavVisible(visible) {
    if (!this.hasSubNavTarget) return
    const isVisible = !this.subNavTarget.classList.contains("hidden")
    if (isVisible === visible) return

    const probe = this.sectionElements[0]
    const before = probe?.getBoundingClientRect().top
    const stuck = this.isMainNavStuck()

    this.subNavTarget.classList.toggle("hidden", !visible)

    if (probe && stuck) {
      const delta = probe.getBoundingClientRect().top - before
      // 2引数形式は常に即時スクロール（behavior: "instant" は Safari 15.3 以下で TypeError になる）
      if (delta !== 0) window.scrollBy(0, delta)
    }
  }

  isMainNavStuck() {
    if (!this.hasMainNavTarget) return false
    const stickyTop = parseFloat(getComputedStyle(this.mainNavTarget).top) || 80
    return this.mainNavTarget.getBoundingClientRect().top <= stickyTop + 1
  }

  setupSubSectionObserver(subcats) {
    this.clearSubSectionObserver()
    this.visibleSubSections.clear()

    subcats.forEach((sub) => {
      const el = document.getElementById(`subcat-${sub.id}`)
      if (el) {
        this.subSectionObserver.observe(el)
        this.observedSubSections.push(el)
      }
    })
  }

  clearSubSectionObserver() {
    this.observedSubSections.forEach((el) => {
      this.subSectionObserver.unobserve(el)
    })
    this.observedSubSections = []
    this.visibleSubSections.clear()
  }

  updateActiveSubButton() {
    if (this.programmaticScroll) return
    if (this.visibleSubSections.size === 0) return

    // 画面上で一番上にあるサブセクションを見つける
    const topVisible = this.observedSubSections.find((el) =>
      this.visibleSubSections.has(el.id)
    )
    if (topVisible) {
      const subcatId = topVisible.id.replace("subcat-", "")
      this.activateSubButton(subcatId)
    }
  }

  activateSubButton(activeId) {
    const buttons = this.subNavInnerTarget.querySelectorAll(".subcategory-btn")
    buttons.forEach((btn) => {
      if (btn.dataset.subcatId === activeId) {
        btn.classList.add("active")
        this.scrollSubBtnIntoView(btn)
      } else {
        btn.classList.remove("active")
      }
    })
  }

  scrollTabIntoView(tab) {
    const container = tab.parentElement
    const scrollLeft = tab.offsetLeft - (container.offsetWidth / 2) + (tab.offsetWidth / 2)
    container.scrollTo({ left: scrollLeft, behavior: "smooth" })
  }

  scrollSubBtnIntoView(btn) {
    const container = btn.parentElement
    const scrollLeft = btn.offsetLeft - (container.offsetWidth / 2) + (btn.offsetWidth / 2)
    container.scrollTo({ left: scrollLeft, behavior: "smooth" })
  }

  scrollOffset() {
    // ヘッダー + メインナビ + サブナビの高さを考慮
    let offset = 120
    if (this.hasSubNavTarget && !this.subNavTarget.classList.contains("hidden")) {
      offset += this.subNavTarget.offsetHeight
    }
    return offset
  }

  // スクロール完了後にクリックした状態を再適用してチラつきを防止
  // scrollend直後にObserverが中間セクションを拾うのを防ぐ
  _startProgrammaticScroll(targetHash, targetSubId = null) {
    this.programmaticScroll = true
    this._scrollTargetHash = targetHash
    this._scrollTargetSubId = targetSubId

    // 前回のタイマーをクリア
    if (this._scrollEndTimer) clearTimeout(this._scrollEndTimer)
    if (this._scrollEndListener) {
      window.removeEventListener("scrollend", this._scrollEndListener)
    }

    this._scrollEndListener = () => {
      this._finishProgrammaticScroll()
      window.removeEventListener("scrollend", this._scrollEndListener)
    }
    window.addEventListener("scrollend", this._scrollEndListener)

    // scrollend 非対応ブラウザ（iOS Safari 18 未満など）用フォールバック:
    // 固定時間ではなく「スクロールが止まった」ことを検知する（長距離のスムーズスクロールを途中で打ち切らないため）
    this._clearSettleWatcher()
    let lastY = window.scrollY
    let lastMove = performance.now()
    this._settleInterval = setInterval(() => {
      const now = performance.now()
      if (window.scrollY !== lastY) {
        lastY = window.scrollY
        lastMove = now
        return
      }
      if (now - lastMove >= 250) this._finishProgrammaticScroll()
    }, 50)
    // 念のための上限
    this._scrollEndTimer = setTimeout(() => { this._finishProgrammaticScroll() }, 5000)
  }

  _clearSettleWatcher() {
    if (this._settleInterval) {
      clearInterval(this._settleInterval)
      this._settleInterval = null
    }
  }

  _finishProgrammaticScroll() {
    if (this._scrollEndTimer) {
      clearTimeout(this._scrollEndTimer)
      this._scrollEndTimer = null
    }
    this._clearSettleWatcher()
    if (this._scrollEndListener) {
      window.removeEventListener("scrollend", this._scrollEndListener)
      this._scrollEndListener = null
    }

    // スクロール完了後、クリックしたタブ/サブカテゴリの状態を再適用
    if (this._scrollTargetHash) {
      this.activateByHash(this._scrollTargetHash)
    }
    if (this._scrollTargetSubId) {
      this.activateSubButton(this._scrollTargetSubId)
    }

    // Observerが安定するまで少し待ってからフラグ解除
    setTimeout(() => {
      this.programmaticScroll = false
      this._scrollTargetHash = null
      this._scrollTargetSubId = null
    }, 150)
  }

  allLabel() {
    // htmlのlang属性からロケールを判定
    const lang = document.documentElement.lang || "ja"
    const labels = { ja: "全て", en: "All", zh: "全部", ko: "전체", th: "ทั้งหมด" }
    return labels[lang] || "全て"
  }
}
